// The room editor (developers): rearrange a shop, a hall or the cottage. Click a piece to pick it (or drag it),
// arrow keys nudge it, R turns it, +/- resize it, Delete removes it; pick something from the list to add it.
// You work in a private copy of the room; Save changes it for everyone.
import { FURNITURE_KINDS, interiorDef, type Furniture, type FurnitureKind } from '../../../shared/interiors.js';

const $ = <T extends HTMLElement = HTMLElement>(id: string) => document.getElementById(id) as T;
const NAMES: Partial<Record<FurnitureKind, string>> = { shelf_bread: 'Bread shelf', shelf_books: 'Book shelf', fishtub: 'Fish tub' };
const nice = (k: FurnitureKind) => NAMES[k] ?? k[0].toUpperCase() + k.slice(1);

export class RoomEditor {
  room: string | null = null;
  list: Furniture[] = [];
  sel = -1;
  private orig: Furniture[] = [];
  private box: HTMLElement;
  onPreview: (room: string, list: Furniture[]) => void = () => {};
  onSave: (list: Furniture[]) => void = () => {};
  onCancel: () => void = () => {};

  constructor() {
    this.box = document.createElement('section'); this.box.id = 'roomEdit'; this.box.className = 'room-edit'; this.box.hidden = true;
    this.box.innerHTML = `<header><b id="reTitle">Room editor</b></header>
      <p class="re-hint">Click a piece (or drag it). Arrows move · R turns · + / − size · Delete removes.</p>
      <div id="reSel" class="re-sel">Nothing picked.</div>
      <label class="re-add">Add <select id="reKind"></select><button type="button" class="btn" id="reAdd">Add</button></label>
      <div class="re-row"><button type="button" class="btn primary" id="reSave">Save for everyone</button><button type="button" class="btn" id="reUndo">Start over</button><button type="button" class="btn" id="reCancel">Cancel</button></div>`;
    document.body.append(this.box);
    const sel = $<HTMLSelectElement>('reKind');
    for (const k of FURNITURE_KINDS) { const o = document.createElement('option'); o.value = k; o.textContent = nice(k); sel.append(o); }
    $('reAdd').addEventListener('click', () => this.add(sel.value as FurnitureKind));
    $('reSave').addEventListener('click', () => this.onSave(this.list.map(f => ({ ...f }))));
    $('reUndo').addEventListener('click', () => { this.list = this.orig.map(f => ({ ...f })); this.sel = -1; this.changed(); });
    $('reCancel').addEventListener('click', () => { this.restore(); this.onCancel(); });
    addEventListener('keydown', e => {
      if (!this.room || /INPUT|TEXTAREA|SELECT/.test((document.activeElement as HTMLElement)?.tagName || '')) return;
      const f = this.list[this.sel]; if (!f) return;
      const mv: Record<string, [number, number]> = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] };
      if (mv[e.code]) { f.x += mv[e.code][0]; f.y += mv[e.code][1]; }
      else if (e.code === 'KeyR') [f.w, f.h] = [f.h, f.w];
      else if (e.key === '+' || e.key === '=') f.w = Math.min(6, f.w + 1);
      else if (e.key === '-') f.w = Math.max(1, f.w - 1);
      else if (e.code === 'Delete' || e.code === 'Backspace') { this.list.splice(this.sel, 1); this.sel = -1; }
      else return;
      e.preventDefault(); e.stopImmediatePropagation(); this.clamp(f); this.changed();
    }, true);
  }

  get active(): boolean { return !!this.room; }
  open(room: string): void {
    const def = interiorDef(room); if (!def) return;
    this.room = room; this.orig = def.furniture.map(f => ({ ...f })); this.list = this.orig.map(f => ({ ...f })); this.sel = -1;
    $('reTitle').textContent = `Rearranging: ${def.name}`; this.box.hidden = false; this.render();
  }
  close(): void { this.room = null; this.box.hidden = true; this.sel = -1; }
  /** Back to how it was (cancel). */
  restore(): void { if (this.room) this.onPreview(this.room, this.orig); }

  /** A click on the room (tile coordinates): pick what's there; `drag` moves the picked piece there. */
  pick(tx: number, ty: number): void {
    const i = this.list.map((f, k) => [f, k] as const).reverse().find(([f]) => tx >= f.x && tx < f.x + f.w && ty >= f.y && ty < f.y + f.h)?.[1] ?? -1;
    this.sel = i; this.render();
  }
  drag(tx: number, ty: number, grab: [number, number]): void { const f = this.list[this.sel]; if (!f) return; const nx = tx - grab[0], ny = ty - grab[1]; if (nx === f.x && ny === f.y) return; f.x = nx; f.y = ny; this.clamp(f); this.changed(); }

  private add(kind: FurnitureKind): void {
    const def = interiorDef(this.room ?? ''); if (!def) return;
    const wall = kind === 'window' || kind === 'banner' || kind === 'sconce';
    const f: Furniture = { kind, x: Math.floor(def.w / 2), y: wall ? 1 : 3, w: ['counter', 'table', 'shelf_bread', 'shelf_books', 'workbench', 'window'].includes(kind) ? 2 : 1, h: kind === 'bed' || kind === 'forge' || kind === 'loom' ? 2 : 1, block: !['window', 'banner', 'sconce', 'rug', 'stool', 'bench'].includes(kind) };
    if (kind === 'rug') f.color = '#db614f';
    this.list.push(f); this.sel = this.list.length - 1; this.clamp(f); this.changed();
  }
  private clamp(f: Furniture): void {
    const def = interiorDef(this.room ?? ''); if (!def) return;
    const wall = f.kind === 'window' || f.kind === 'banner' || f.kind === 'sconce';
    f.x = Math.max(1, Math.min(def.w - 1 - f.w, f.x)); f.y = Math.max(wall ? 1 : 2, Math.min(def.h - 1 - f.h, f.y));
  }
  private changed(): void { if (this.room) this.onPreview(this.room, this.list); this.render(); }
  private render(): void {
    const f = this.list[this.sel];
    $('reSel').textContent = f ? `${nice(f.kind)} at ${f.x}, ${f.y} · ${f.w}×${f.h}${f.block ? '' : ' · walk-through'}` : `Nothing picked. ${this.list.length} pieces in the room.`;
  }
}
